// Copyright 2026 Zibby Lab. Apache-2.0.
//
// DangerZone — shared destructive-action section for detail pages.
//
// Renders the "Danger zone" heading + an explanatory hint + a DangerButton
// that opens the styled DestroyAppModal (NOT a native window.confirm). Used
// by both the pipeline Settings tab and the app Settings tab so teardown is
// visually + behaviourally identical across the product.
//
// It owns the modal open/submitting state. The caller supplies an async
// `onConfirm` that performs the actual delete (and typically navigates away
// / toasts on its own); DangerZone drives the busy state and closes the
// modal when it resolves.
//
// Usage (cloud app — plain confirm):
//   <DangerZone
//     label="n8n"
//     hint="This stops the running task and removes the app. This cannot be undone."
//     buttonLabel="Destroy app"
//     onConfirm={() => Apps.destroy(instanceId)}
//   />
//
// Usage (solo app — type-to-confirm data-loss gate):
//   <DangerZone label={slug} requireType buttonLabel="Delete app" onConfirm={...} />

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { trashOutline } from 'ionicons/icons';

import DangerButton from '../DangerButton/DangerButton';
import DestroyAppModal from '../SoloDestroyModal/SoloDestroyModal';
import './DangerZone.css';

export default function DangerZone({
  // When true (default), render a separator rule above the zone — right
  // when it follows other sections. Set false when it stands alone in its
  // own card so the line doesn't look like a mistake.
  separated = true,
  // Section copy.
  heading = 'Danger zone',
  hint,
  buttonLabel,
  // Identity shown in the modal title / type-to-confirm word.
  label,
  // Modal overrides (all optional — DestroyAppModal derives sensible
  // defaults from `label` + `requireType`).
  modalTitle,
  modalWarning,
  requireType = false,
  confirmWord,
  confirmLabel,
  busyLabel,
  // The actual delete. Async; may throw (the modal closes regardless and
  // the caller is expected to surface failures via its own toast).
  onConfirm,
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm?.();
      setOpen(false);
    } catch {
      // onConfirm surfaces its own failure (its toast) and re-throws to
      // signal "not done" — keep the modal open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`danger-zone${separated ? ' danger-zone--separated' : ''}`}>
      <h3 className="danger-zone-title">{heading}</h3>
      {hint && <p className="danger-zone-hint">{hint}</p>}
      <DangerButton icon={trashOutline} onClick={() => setOpen(true)}>
        {buttonLabel}
      </DangerButton>

      <DestroyAppModal
        open={open}
        label={label}
        title={modalTitle}
        warning={modalWarning}
        requireType={requireType}
        confirmWord={confirmWord}
        confirmLabel={confirmLabel}
        busyLabel={busyLabel}
        busy={submitting}
        onClose={() => { if (!submitting) setOpen(false); }}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

DangerZone.propTypes = {
  separated: PropTypes.bool,
  heading: PropTypes.string,
  hint: PropTypes.node,
  buttonLabel: PropTypes.string.isRequired,
  label: PropTypes.string,
  modalTitle: PropTypes.string,
  modalWarning: PropTypes.node,
  requireType: PropTypes.bool,
  confirmWord: PropTypes.string,
  confirmLabel: PropTypes.string,
  busyLabel: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
};
